import React, { useEffect, useState, createContext, useMemo } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

export const QUEUE_CARD_MODES = {
  LAST_FRAME: 'lastFrame',
  RETRANSLATION: 'retranslation',
};

export const DISPLAY_ZONES_OPTIONS = {
  ROI: 'roi',
  DROI: 'droi',
};

export const DOTS_COLORS_OPTIONS = {
  BLACK: 'black',
  POSITIVE: 'positive',
  NEGATIVE: 'negative',
  PRIMARY: 'primary',
};

const queueCardDefaultSettings = {
  mode: QUEUE_CARD_MODES.LAST_FRAME,
  displayZones: [DISPLAY_ZONES_OPTIONS.ROI],
  showDotsOnFaces: {
    enabled: true,
    size: 10,
    color: DOTS_COLORS_OPTIONS.POSITIVE,
  },
};

export const QueueContext = createContext();

export function QueueContextProvider({ children }) {
  const { t } = useTranslation();
  const [queueCardSettings, setQueueCardSettings] = useState(queueCardDefaultSettings);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [streams, setStreams] = useState([]);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [editingGroupName, setEditingGroupName] = useState(null);
  const [isStreamModalOpen, setIsStreamModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('monitoring');
  const [streamIdsForStatistic, setStreamIdsForStatistic] = useState([]);

  const openGroupModal = (name) => {
    setEditingGroupName(name ?? null);
    setIsGroupModalOpen(true);
  };

  const closeGroupModal = () => {
    setIsGroupModalOpen(false);
    setEditingGroupName(null);
  };

  const openStreamModal = (n) => {
    setEditingGroupName(n);
    setIsStreamModalOpen(true);
  };

  const closeStreamModal = () => {
    setIsStreamModalOpen(false);
    setEditingGroupName(null);
  };

  const fetchStreams = async () => {
    try {
      const response = await apiContainer.lunaClient.streams.getAll();

      const groupNames = [];
      const groupedQueues = { no_group: [] };
      response.forEach((stream) => {
        try {
          const { zone_group } = JSON.parse(stream.description);

          if (!zone_group) {
            groupedQueues.no_group.push(stream);
            if (!groupNames.includes('no_group')) groupNames.push('no_group');
          } else {
            if (!groupNames.includes(zone_group)) groupNames.push(zone_group);

            if (!groupedQueues[zone_group]) {
              groupedQueues[zone_group] = [];
            }

            groupedQueues[zone_group].push(stream);
          }
        } catch (error) {
          // Если не удается это значит что поток не является очередью
        }
      });

      setGroups(groupNames.reverse());
      setStreams(groupedQueues);
    } catch (error) {
      console.error(error);
      toast.error(t('queue:queue page.errors.fetch streams'));
    }
  };

  useEffect(() => {
    fetchStreams();
  }, []);

  const queueContextValue = useMemo(() => ({
    streams,
    groups,
    queueCardSettings,
    setQueueCardSettings,
    fetchStreams,
    isGroupModalOpen,
    isStreamModalOpen,
    editingGroupName,
    openGroupModal,
    closeGroupModal,
    openStreamModal,
    closeStreamModal,
    isSettingsModalOpen,
    setIsSettingsModalOpen,
    activeTab,
    setActiveTab,
    streamIdsForStatistic,
    setStreamIdsForStatistic,
  }), [
    editingGroupName,
    groups,
    isGroupModalOpen,
    isStreamModalOpen,
    queueCardSettings,
    streams,
    isSettingsModalOpen,
    activeTab,
    streamIdsForStatistic]);

  return (
    <QueueContext.Provider value={queueContextValue}>
      {children}
    </QueueContext.Provider>
  );
}
